'use client';

import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { reloadsHeld } from '@/lib/reload-guard';
import { notePageLeaving } from '@/lib/page-leaving';
import {
  alreadyOnWorkerBuild,
  updateApplyReason,
  shouldApplyOnReturn,
  type ApplyRule,
  type ApplyTrigger,
} from '@/lib/update-apply';
import { track, flushAnalytics } from '@/lib/analytics/track';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { BAKED_RELEASE, fetchLiveRelease, shouldForceApply } from '@/lib/release/gate';
import { landingPathFor } from '@/lib/landing-path';
import { MOBILE_MQ } from '@/lib/viewport/client';
import { isNative } from '@/lib/native/is-native';

/**
 * Is this the phone in somebody's hand?
 *
 * Read at the moment it is needed rather than once on mount, so a tablet that
 * has been rotated gets the current answer. `useIsMobile` is not available
 * here: UpdatePrompt is mounted in the ROOT layout, outside the
 * ViewportProvider, and would get its defensive `false` — the desktop answer,
 * on every phone.
 */
/**
 * The route right now.
 *
 * Read from `location` rather than held in a ref off `usePathname`, because
 * the service-worker callbacks are registered once and fire minutes later —
 * a ref would need writing on every route change, and writing a ref that a
 * `useCallback` closed over is what `react-hooks/immutability` forbids. The
 * browser's own answer is both simpler and the true one at the moment a worker
 * installs.
 */
function currentPath(): string {
  try {
    return window.location.pathname;
  } catch {
    return '';
  }
}

function isPhone(): boolean {
  if (isNative()) return true;
  try {
    return window.matchMedia(MOBILE_MQ).matches;
  } catch {
    return false;
  }
}

/**
 * The build this page's JavaScript came from: `git rev-parse --short HEAD`,
 * baked in by `next.config.ts`. Read at call time, never at module load, so a
 * test can set it. Written out in full because Next inlines only the literal
 * `process.env.NEXT_PUBLIC_…` expression.
 */
function pageBuild(): string | undefined {
  return process.env.NEXT_PUBLIC_BUILD_HASH;
}

/**
 * How long to wait for a worker to say which build it is. A worker that ships
 * before `GET_BUILD_ID` existed never answers, and then the update goes ahead
 * exactly as it did before; this only bounds how long that takes to decide.
 */
const BUILD_QUERY_TIMEOUT_MS = 1_000;

/**
 * Ask a service worker which build it is (`GET_BUILD_ID` in `public/sw.js`).
 * Resolves to its `BUILD_ID`, or null for no answer, a malformed one, or no
 * `MessageChannel` to ask on. Never rejects.
 */
function askWorkerBuild(worker: ServiceWorker): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    let channel: MessageChannel | null = null;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { channel?.port1.close(); } catch { /* already closed */ }
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), BUILD_QUERY_TIMEOUT_MS);
    try {
      channel = new MessageChannel();
      channel.port1.onmessage = (event: MessageEvent) => {
        const id = (event.data as { buildId?: unknown } | null)?.buildId;
        finish(typeof id === 'string' ? id : null);
      };
      worker.postMessage({ type: 'GET_BUILD_ID' }, [channel.port2]);
    } catch {
      finish(null);
    }
  });
}

/**
 * How often to ask the browser whether a new version has been deployed.
 *
 * Nothing here reloads anything — a check only ever puts a worker into
 * `waiting`, ready for the next navigation. Fifteen minutes because every
 * check that finds a new build downloads and installs a worker, which
 * re-fetches the offline pages, on a phone, and we deploy dozens of times a
 * day. A reopen checks immediately regardless.
 */
const UPDATE_CHECK_MS = 15 * 60_000;

/**
 * The app's updater. It has no UI, and that is the design.
 *
 * 🚨 **NOBODY IS EVER ASKED, AND NOBODY IS EVER INTERRUPTED.** A pending update
 * is applied at a moment the page was being torn down anyway:
 *
 *   1. On a NAVIGATION HOME — `/overview` on a phone, `/schedule` on a desktop.
 *      The FIRST arrival, which is the landing after a login or a cold start,
 *      takes it straight away: nothing is in progress at the start of a session
 *      and it buys the whole day on current code. Later arrivals wait until the
 *      client has been up half an hour, and any page will do once it is four
 *      hours old — the backstop that stops the pin failing silently for
 *      somebody who never goes home.
 *   2. On a RETURN after a real absence — he has picked the phone up.
 *   3. On a COLD START, which takes no code: with no client left to control, a
 *      waiting worker activates on its own.
 *
 * The only exception is a forced update, bumped by hand for a fix people are
 * being hurt by (`src/lib/release/gate.ts`). Even that cannot interrupt a live
 * voice call — a reload hold outranks everything.
 *
 * ## What was here before
 *
 * A bar reading *"New version available — Refresh"*, and a release number cut
 * nightly to stop it appearing hourly. Both are gone. `update-apply.ts` carries
 * the reasoning; the short version is that rationing a question is not the same
 * as not asking one, and an owner does not want to be told his software has
 * been updated. He wants it to have been.
 *
 * 🚨 The half that mattered most was NATIVE. `sw.js` stopped calling
 * `skipWaiting()` on install, which fixed the browser, while the WebView went
 * on applying updates the instant a worker installed — silently, mid-shift, on
 * the one client where the work actually happens. That is closed here too: the
 * WebView takes the same three moments as everything else, and
 * `src/lib/reload-guard.ts` records what an unannounced reload costs.
 */
export default function UpdatePrompt() {
  const pathname = usePathname();

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  // Refs rather than state throughout: nothing renders, and the listeners below
  // are registered once, so a state value would be the one they saw on mount
  // for the rest of the page's life.
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);
  // Set only when WE asked for the update to be applied. `controllerchange`
  // fires for other reasons too — another tab of the same app applying it, say
  // — and those must not reload this one out from under its user.
  const applyRequestedRef = useRef(false);
  // When the page went hidden. `null` while it is visible.
  const hiddenAtRef = useRef<number | null>(null);
  // When this client started. The navigation rule needs an uptime to compare
  // against. Stamped in the mount effect below rather than here: reading a
  // clock during render is impure, and `react-hooks/purity` says so.
  const startedAtRef = useRef<number | null>(null);
  // Has somebody bumped the forced-update number past ours? Null until the
  // first answer arrives, and never true on a failed check.
  const forcedRef = useRef(false);
  // Set when somebody ELSE's client activated the update, so this page runs
  // stale code with no waiting worker of its own to point at. A plain reload is
  // then the only way to pick the new code up.
  const staleRef = useRef(false);

  // A waiting worker already found to be this page's own build. Asked once:
  // the answer cannot change for the same worker, and every later rule that
  // fires for it would only be asking again.
  const currentWorkerRef = useRef<ServiceWorker | null>(null);
  // A build question in flight, so two rules firing together ask once.
  const askingRef = useRef(false);

  /**
   * Apply a pending update. Reloading is the LAST step, not the first: the
   * waiting worker has to take control before a reload gets the new code, so
   * we ask it to skip waiting and let `controllerchange` do the reload.
   */
  const commitApply = useCallback((rule: ApplyRule, uptimeMs: number) => {
    /*
     * 🚨 Recorded AND flushed, because applying an update reloads the page and
     * Amplitude batches on a timer — the last event before a hard navigation is
     * the one that gets lost, which is exactly this one.
     *
     * Without this pair nothing about the updater is observable: a rule that
     * never fires and a rule that fires every ten minutes produce the same
     * silence, and the first report either way is a customer saying "it keeps
     * refreshing". `rule` is what makes the four thresholds tunable rather
     * than guessed at.
     */
    track(ANALYTICS_EVENTS.APP_UPDATE_APPLIED, {
      rule,
      uptime_ms: uptimeMs,
      // The route, not the URL: a query string can carry a client reference.
      path: currentPath(),
    });
    flushAnalytics();
    applyRequestedRef.current = true;
    const worker = waitingWorkerRef.current;
    if (worker) {
      worker.postMessage({ type: 'SKIP_WAITING' });
      return;
    }
    // No waiting worker means it already activated and we held the reload back.
    // The page is running old code against the new worker, so a plain reload is
    // all that is left.
    notePageLeaving('update');
    window.location.reload();
  }, []);

  /**
   * Apply a pending update — unless this page is ALREADY on the build it would
   * load.
   *
   * 🚨 A cold start fetches the document from the network, so the page is
   * usually current already, and the worker installing in those same seconds
   * is that same build. The landing rule reloaded it anyway: on 11 Oct 2026 an
   * engineer tapped Schedule two seconds after opening the app and was put
   * back on Overview, with the Schedule chunk cancelled mid-download and
   * reported as a fault (MUCKA-WEB-CV). A reload that cannot change the code
   * is all cost. So the worker is asked which build it is, and a page on that
   * build is left alone; the worker stays waiting, and a later deploy's worker
   * replaces it and is asked in turn.
   *
   * Exactly as before, deliberately:
   *   · a FORCED update reloads without asking — it is somebody's deliberate
   *     act, and the one path allowed to interrupt;
   *   · the reload hold is checked by every caller first, and again here
   *     after the question, because a call can start while it is answered;
   *   · no answer (an older worker, no `MessageChannel`, a page build of
   *     `dev`) means reload, as it always did. Only a definite match skips.
   */
  const applyUpdate = useCallback((rule: ApplyRule, uptimeMs: number) => {
    const page = pageBuild();
    const worker = waitingWorkerRef.current
      ?? (staleRef.current ? navigator.serviceWorker?.controller ?? null : null);
    if (rule === 'forced' || !page || page === 'dev' || !worker) {
      commitApply(rule, uptimeMs);
      return;
    }
    if (worker === currentWorkerRef.current) return;
    if (askingRef.current) return;
    askingRef.current = true;
    void askWorkerBuild(worker).then((buildId) => {
      askingRef.current = false;
      if (alreadyOnWorkerBuild(page, buildId)) {
        currentWorkerRef.current = worker;
        // Somebody else's activation of our own build leaves nothing stale.
        if (worker !== waitingWorkerRef.current) staleRef.current = false;
        track(ANALYTICS_EVENTS.APP_UPDATE_NOT_NEEDED, { rule, uptime_ms: uptimeMs, path: currentPath() });
        return;
      }
      if (reloadsHeld()) return;
      commitApply(rule, uptimeMs);
    });
  }, [commitApply]);

  /**
   * Ask whether a pending update can be applied, and apply it if so.
   *
   * 🚨 Called from BOTH ends: when the route changes, and when a worker becomes
   * available. Either alone is a bug. Route-only misses the login case
   * entirely — `RootLandingRedirect` lands somebody on home within
   * milliseconds while a new worker takes seconds to install, so the one
   * evaluation happens before there is anything to apply and never repeats.
   * Worker-only would never notice somebody walking home half an hour later.
   */
  /**
   * A new version has been parked. Recorded once per client.
   *
   * DETECTED without a matching APPLIED is the staleness measure, and it is the
   * only way "this rule never fires" becomes a query rather than a customer
   * complaint. The login rule shipped able to fire exactly never and every test
   * was green; this is what would have shown it.
   */
  const detectedRef = useRef(false);
  const noteDetected = useCallback(() => {
    if (detectedRef.current) return;
    detectedRef.current = true;
    track(ANALYTICS_EVENTS.APP_UPDATE_DETECTED, { path: currentPath() });
  }, []);

  const maybeApply = useCallback((trigger: ApplyTrigger) => {
    if (startedAtRef.current === null) return;
    const uptimeMs = Date.now() - startedAtRef.current;
    const rule = updateApplyReason({
      trigger,
      uptimeMs,
      toLandingPage: currentPath() === landingPathFor(isPhone()),
      reloadsHeld: reloadsHeld(),
      updatePending: waitingWorkerRef.current !== null || staleRef.current,
      forced: forcedRef.current,
    });
    if (rule) applyUpdate(rule, uptimeMs);
  }, [applyUpdate]);

  /**
   * A navigation happened.
   *
   * 🚨 Deliberately NOT on the first render. The mount render is a cold start,
   * where a waiting worker activates by itself; `maybeApply` is driven from the
   * worker side for that case instead.
   */
  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    maybeApply('navigation');
  }, [pathname, maybeApply]);

  useEffect(() => {
    startedAtRef.current = Date.now();
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // Local dev: never run the production service worker. The dev server's
    // static chunk URLs are stable, so a cache-first SW serves the version it
    // cached early and stale CSS/JS persists forever — even after a hard
    // refresh or in incognito. Skip registration and actively unregister +
    // purge caches left over from a prior visit so dev always sees fresh code.
    // (Vercel deploys run NODE_ENV=production, so they still get the SW.)
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .then(() => {
          if (typeof caches !== 'undefined') {
            return caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
          }
        })
        .catch(() => {});
      return;
    }

    /**
     * Ask whether a forced update has been declared, and act on it now rather
     * than waiting for a navigation. This is the only path that reloads
     * somebody who is sitting still, so it is the only one that needed a
     * deliberate human act behind it.
     */
    function checkForced(): Promise<void> {
      return fetchLiveRelease().then((live) => {
        const forced = shouldForceApply({ baked: BAKED_RELEASE, live: live?.release ?? null });
        forcedRef.current = forced;
        if (!forced) return;
        if (!waitingWorkerRef.current && !staleRef.current) return;
        maybeApply('worker');
      });
    }

    void checkForced();

    navigator.serviceWorker.register('/sw.js').then((registration) => {
      registrationRef.current = registration;

      // Already waiting (the page was open during a deploy, or the last
      // session left one behind). Ask straight away: on a login landing this is
      // the call that actually does the work.
      if (registration.waiting) {
        waitingWorkerRef.current = registration.waiting;
        noteDetected();
        maybeApply('worker');
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            waitingWorkerRef.current = newWorker;
            noteDetected();
            // 🚨 Ask NOW as well as on the next navigation. At a login the
            // arrival at home is already minutes-old news by the time a worker
            // finishes installing, and a rule that only runs on route changes
            // would never be asked again. The trigger keeps this honest: from
            // here only the landing window and a forced update can fire.
            maybeApply('worker');
          }
        });
      });
    }).catch(() => {
      // SW registration failed — not critical.
    });

    /**
     * Hidden / visible.
     *
     * Going away starts the clock that decides whether coming back counts as
     * REOPENING the app; coming back checks for a new version and applies a
     * pending one if the absence was real. `shouldApplyOnReturn` owns that.
     */
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;

      if (registrationRef.current) {
        registrationRef.current.update().catch(() => {});
      }
      void checkForced();

      if (hiddenAt === null) return;
      if (
        shouldApplyOnReturn({
          hiddenForMs: Date.now() - hiddenAt,
          reloadsHeld: reloadsHeld(),
          updatePending: waitingWorkerRef.current !== null || staleRef.current,
        })
      ) {
        applyUpdate('return', Date.now() - (startedAtRef.current ?? Date.now()));
      }
    }

    // Periodic check. Nothing here reloads on its own: it puts a worker into
    // `waiting` for the next navigation, and asks whether anything has been
    // declared urgent enough to skip that wait.
    const interval = setInterval(() => {
      if (registrationRef.current) {
        registrationRef.current.update().catch(() => {});
      }
      void checkForced();
    }, UPDATE_CHECK_MS);

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Reload when the controller changes — but ONLY when we asked for it.
    //
    // 🚨 `controllerchange` fires for three different things and only one of
    // them is "we applied this update":
    //
    //   · WE called applyUpdate() → skipWaiting → the new worker claimed us.
    //     Reload; this is the case the reload exists for.
    //   · SOMEBODY ELSE'S client applied it — another tab, or a freshly
    //     cold-started one. This page is now running stale code, which the next
    //     navigation will fix. It is not worth reloading out from under
    //     whatever the user is doing this second.
    //   · the FIRST install claiming a page that had no controller → nothing to
    //     update TO. The page is already the newest code there is.
    //
    // sw.js calls clients.claim() on activate, so the third case fires on EVERY
    // first visit — a new browser, a cleared profile, a fresh incognito window.
    // The page would silently reload itself a second or two after landing, and
    // anyone who started typing straight away lost it. On /login that reads as
    // "I entered my password and the screen blanked". Found by the E2E smoke
    // suite, which is now the regression test for it.
    const wasControlled = Boolean(navigator.serviceWorker.controller);
    function onControllerChange() {
      // "Did we ask for this?" comes FIRST, before the wasControlled guard.
      // `wasControlled` is read once at mount, and a page can have no
      // controller while a NEW worker sits waiting — most obviously after a
      // hard refresh, which bypasses the service worker. Reversing these two
      // lines is what made the old Refresh button dead for three weeks.
      if (applyRequestedRef.current) {
        // Said first, because every request this page still has in flight
        // fails the moment the reload starts, and what reports those failures
        // should know why (`page-leaving.ts`, MUCKA-WEB-CV / -C3).
        notePageLeaving('update');
        window.location.reload();
        return;
      }
      if (!wasControlled) return;
      // Someone else activated it. We are stale but we are not interrupting;
      // the next navigation reloads us.
      waitingWorkerRef.current = null;
      staleRef.current = true;
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, [applyUpdate, maybeApply, noteDetected]);

  // No UI. An update the user has to be told about is one we got wrong.
  return null;
}
